import { motion } from "framer-motion";
import { CreditCard, Search, Smartphone, Star } from "lucide-react";
import { HOW_IT_WORKS } from "../../data/content";
import { reveal } from "../../lib/motion";

const BULLETS = [
  "Descubra profissionais cristãs perto de você",
  "Agende e pague com a Wallet AURA",
  "Ganhe cashback em cada serviço",
  "Cartão AURA e maquininha POS integrados",
];

const PhoneMockup = () => (
  <div
    data-testid="phone-mockup"
    className="w-[270px] rounded-[2.5rem] bg-ink p-2.5 shadow-[0_40px_90px_rgba(26,24,22,0.35)] sm:w-[300px]"
  >
    <div className="relative overflow-hidden rounded-[2rem] bg-cream px-5 pb-5 pt-8">
      <div className="absolute left-1/2 top-2.5 h-5 w-24 -translate-x-1/2 rounded-full bg-ink" />
      <p className="mt-4 font-serif text-xl text-ink">Bom dia, Ana Beatriz</p>
      <div className="mt-4 flex items-center gap-2 rounded-full border border-gold-border bg-white px-4 py-2.5">
        <Search className="h-3.5 w-3.5 text-mutedink" />
        <span className="text-xs text-mutedink">Buscar profissional cristã...</span>
      </div>
      <div className="mt-3 flex gap-2">
        {["Beleza", "Saúde", "Moda"].map((chip, i) => (
          <span
            key={chip}
            className={`rounded-full px-3.5 py-1.5 text-[10px] font-semibold ${
              i === 0 ? "bg-teal text-white" : "bg-teal-light text-teal-deep"
            }`}
          >
            {chip}
          </span>
        ))}
      </div>
      {[
        { name: "Dra. Camila R.", role: "Dermatologista", distance: "1,2 km" },
        { name: "Studio Essência", role: "Beleza & Estética", distance: "2,8 km" },
      ].map((pro) => (
        <div
          key={pro.name}
          className="mt-3 flex items-center gap-3 rounded-2xl border border-gold-border bg-white p-3.5"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gold-light font-serif text-sm text-gold">
            {pro.name.charAt(0)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-semibold text-ink">{pro.name}</p>
            <p className="text-[10px] text-mutedink">
              {pro.role} · {pro.distance}
            </p>
          </div>
          <span className="flex items-center gap-1 text-[10px] font-semibold text-gold">
            <Star className="h-3 w-3 fill-gold" /> 4,9
          </span>
        </div>
      ))}
      <div className="mt-4 rounded-2xl bg-teal px-4 py-3 text-center">
        <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-champagne">
          Cashback AURA
        </p>
        <p className="font-serif text-lg text-white">R$ 48,20</p>
      </div>
    </div>
  </div>
);

export const Technology = () => {
  return (
    <>
      {/* Seção 8 — Tecnologia */}
      <section
        data-testid="technology-section"
        className="overflow-hidden bg-creamalt py-16 md:py-24 lg:py-32"
      >
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <motion.p
              {...reveal()}
              className="text-xs font-semibold uppercase tracking-[0.3em] text-gold"
            >
              A Tecnologia
            </motion.p>
            <motion.h2
              {...reveal(0.1)}
              className="mt-4 font-serif text-3xl leading-tight text-ink sm:text-4xl lg:text-5xl"
            >
              "Tudo o que você precisa.{" "}
              <em className="italic text-teal">Em uma rede feita para você."</em>
            </motion.h2>
            <motion.p
              {...reveal(0.2)}
              className="mt-6 text-base leading-relaxed text-mutedink sm:text-lg"
            >
              O aplicativo AURA une descoberta, agendamento e pagamento em uma
              experiência elegante e segura. Encontre profissionais verificadas,
              agende com um toque, pague pela Wallet AURA e ainda ganhe cashback
              para servir mais.
            </motion.p>
            <motion.ul {...reveal(0.3)} className="mt-8 space-y-4">
              {BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-3 text-base text-ink">
                  <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-gold" />
                  {b}
                </li>
              ))}
            </motion.ul>
          </div>

          <motion.div {...reveal(0.2)} className="relative flex justify-center lg:justify-end">
            <div className="relative pr-0 lg:pr-10">
              <PhoneMockup />
              <motion.div
                data-testid="badge-cartao-aura"
                animate={{ y: [0, -10, 0] }}
                transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
                className="absolute -left-16 top-16 w-52 rounded-2xl border border-gold-border bg-white p-5 shadow-[0_20px_60px_rgba(197,160,89,0.12)] sm:-left-24"
              >
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-teal" />
                  <p className="text-xs font-semibold text-ink">Cartão AURA</p>
                </div>
                <p className="mt-2 text-[11px] leading-relaxed text-mutedink">
                  Débito e crédito para usar seus bônus e ganhos
                </p>
                <span className="mt-3 inline-block rounded-full bg-gold-light px-3 py-1 text-[9px] font-semibold uppercase tracking-[0.2em] text-gold">
                  Em breve
                </span>
              </motion.div>
              <motion.div
                data-testid="badge-pos-aura"
                animate={{ y: [0, 10, 0] }}
                transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
                className="absolute -left-16 bottom-16 w-52 rounded-2xl border border-gold-border bg-white p-5 shadow-[0_20px_60px_rgba(197,160,89,0.12)] sm:-left-24"
              >
                <div className="flex items-center gap-2">
                  <Smartphone className="h-4 w-4 text-teal" />
                  <p className="text-xs font-semibold text-ink">Maquininha POS AURA</p>
                </div>
                <p className="mt-2 text-[11px] leading-relaxed text-mutedink">
                  Receba pagamentos direto na plataforma
                </p>
                <span className="mt-3 inline-block rounded-full bg-gold-light px-3 py-1 text-[9px] font-semibold uppercase tracking-[0.2em] text-gold">
                  Em breve
                </span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Seção 9 — Como Funciona */}
      <section
        data-testid="how-it-works-section"
        className="bg-cream py-16 md:py-24 lg:py-32"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
          <motion.p
            {...reveal()}
            className="text-center text-xs font-semibold uppercase tracking-[0.3em] text-gold"
          >
            Como Funciona
          </motion.p>
          <motion.h2
            {...reveal(0.1)}
            className="mt-4 text-center font-serif text-3xl text-ink sm:text-4xl lg:text-5xl"
          >
            Simples como <em className="italic text-teal">deve ser</em>
          </motion.h2>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 lg:gap-8">
            {HOW_IT_WORKS.map((s, i) => (
              <motion.div
                key={s.num}
                {...reveal(0.1 * (i + 1))}
                data-testid={`step-card-${s.num}`}
                className="group relative overflow-hidden rounded-3xl border border-gold-border bg-white p-8 transition-all duration-300 hover:-translate-y-1.5 hover:border-gold hover:shadow-[0_20px_60px_rgba(197,160,89,0.12)]"
              >
                <span className="absolute -right-2 -top-4 font-serif text-8xl text-gold/10">
                  {s.num}
                </span>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">
                  {s.num}
                </p>
                <h3 className="mt-4 font-serif text-2xl text-ink">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-mutedink">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};
